/**
 * Interactive In-Flight Temporary Drawing Previews.
 *
 * Constructs temporary drawing preview objects during mouse move dragging.
 */

import { POSITION_COLORS, POSITION_TYPE, defaultTarget, isValidSecondClick } from "./position_tool";

export interface ChartPoint {
  time: number;
  price: number;
}

export type TempDrawingPreview = Record<string, any>;

/**
 * Creates temporary in-flight drawing preview objects for mouse hover/drag tracking.
 */
export function makeTempDrawingPreview(
  activeTool: string,
  startPoint: ChartPoint | null | undefined,
  currentPoint: ChartPoint,
  secondStartPoint?: ChartPoint | null | undefined,
): TempDrawingPreview | null {
  if (!startPoint) return null;

  switch (activeTool) {
    case "trendline":
      return {
        type: "trendline",
        point1: startPoint,
        point2: currentPoint,
        color: "#787B86",
        lineWidth: 2,
        lineStyle: 0,
        opacity: 1,
      };

    case "rect":
      return {
        type: "rect",
        point1: startPoint,
        point2: currentPoint,
        color: "#787B86",
        lineWidth: 2,
        lineStyle: 0,
        opacity: 1,
      };

    case "fib":
      return {
        type: "fib",
        point1: startPoint,
        point2: currentPoint,
        color: "#787B86",
        lineWidth: 2,
        opacity: 1,
      };

    case "extended_line":
      return {
        type: "extended_line",
        p1: startPoint,
        p2: currentPoint,
        color: "#787B86",
        lineWidth: 2,
        lineStyle: 0,
        opacity: 1,
      };

    case "ray":
      return {
        type: "ray",
        p1: startPoint,
        p2: currentPoint,
        color: "#787B86",
        lineWidth: 2,
        lineStyle: 0,
        opacity: 1,
      };

    case "hray":
      return {
        type: "hray",
        p1: startPoint,
        p2: currentPoint,
        color: "#787B86",
        lineWidth: 2,
        lineStyle: 0,
        opacity: 1,
      };

    case "arrow":
      return {
        type: "arrow",
        p1: startPoint,
        p2: currentPoint,
        color: "#787B86",
        lineWidth: 2,
        lineStyle: 0,
        opacity: 1,
      };

    case "parallel_channel":
      if (!secondStartPoint) {
        return {
          type: "extended_line",
          p1: startPoint,
          p2: currentPoint,
          color: "#787B86",
          lineWidth: 2,
          lineStyle: 1,
          opacity: 1,
        };
      }
      return {
        type: "parallel_channel",
        p1: startPoint,
        p2: secondStartPoint,
        p3: currentPoint,
        color: "#787B86",
        fillColor: "#787B86",
        lineWidth: 2,
        lineStyle: 1,
        opacity: 1,
      };

    case "text":
      return {
        type: "text",
        p1: startPoint,
        text: "Text",
        fontSize: 14,
        color: "#E7EBF0",
        bgColor: "#171A1E",
      };

    case "price_range":
      return {
        type: "price_range",
        p1: startPoint,
        p2: currentPoint,
        color: "#787B86",
        lineWidth: 2,
        lineStyle: 1,
        opacity: 1,
      };

    case "long_position":
    case "short_position": {
      // Rubber-band for the Long/Short Position tool: startPoint = entry, the
      // cursor = the candidate stop. Drawn ONLY on the valid side of entry and
      // after it in time, so the wrong side is visibly not a drawing.
      const side = activeTool === "long_position" ? "long" : "short";
      if (!isValidSecondClick(side, startPoint, currentPoint)) return null;
      const target = defaultTarget(side, startPoint.price, currentPoint.price);
      if (target === null) return null;
      return {
        type: POSITION_TYPE,
        side,
        p1: startPoint,
        p2: currentPoint,
        p3: { time: currentPoint.time, price: target },
        color: POSITION_COLORS.entry,
        profitColor: POSITION_COLORS.profit,
        lossColor: POSITION_COLORS.loss,
        lineWidth: 2,
        lineStyle: 1,
        opacity: 1,
      };
    }

    default:
      return null;
  }
}
